// src/components/MenuOverlay.js
import React, { useEffect, useRef, useMemo } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PRIMARY_NAV_ITEMS } from "../content/siteNavigation";
import { trackEvent } from "../lib/analytics";

const Z = 50;

const Backdrop = styled(motion.button)`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.55);
  border: 0; padding: 0; margin: 0;
  cursor: default;
  z-index: ${Z};
`;

const Shell = styled(motion.div)`
  position: fixed; inset: 0;
  z-index: ${Z + 1};
  pointer-events: none;
`;

const Sheet = styled(motion.div)`
  position: absolute; inset: 0;
  pointer-events: auto;
  background:
    radial-gradient(1200px 1200px at 92% 6%, rgba(255,255,255,0.08), rgba(255,255,255,0.02) 40%, rgba(255,255,255,0.00) 60%),
    linear-gradient(180deg, rgba(18,18,18,.97), rgba(10,10,10,.98));
  border: 1px solid rgba(255,255,255,0.10);
  overflow: hidden;
`;

const Grid = styled.div`
  height: 100%; width: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  padding: clamp(16px, 3vw, 24px);
`;

const Row = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
`;

const Brand = styled.div`
  display: flex; align-items: center; gap: 12px;
  img { height: 28px; width: auto; }
  font-family: var(--font-heading);
  font-weight: 700;
  letter-spacing: .08em;
  font-size: 0.86rem;
  opacity: 0.9;
`;

const CloseBtn = styled(motion.button)`
  all: unset; cursor: pointer;
  height: 40px; width: 40px; border-radius: 999px;
  display: grid; place-items: center;
  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.06);
  transition: transform .18s ease;
  &:hover { transform: translateY(-1px); }
`;

const ListWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: clamp(18px, 5vh, 56px) 0;
`;

const List = styled(motion.ul)`
  list-style: none; margin: 0; padding: 0;
  display: grid;
  gap: clamp(8px, 1.8vh, 14px);
  text-align: left;
  width: fit-content;
  margin-left: clamp(32px, 8vw, 120px);

  @media (max-width: 768px) {
    margin-left: clamp(12px, 4vw, 20px);
  }
`;

const Item = styled(motion.li)``;

const LinkBtn = styled(motion.button)`
  all: unset; cursor: pointer;
  font-family: var(--font-heading);
  font-size: clamp(24px, 4.4vw, 52px);
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.045em;
  position: relative;
  padding: 0.06em 0;
  text-shadow: 0 2px 12px rgba(0,0,0,.45);
  /* shimmer underline on hover */
  &:after{
    content:"";
    position:absolute; left: 0; right: 100%; bottom: -6px; height: 2px;
    background: linear-gradient(90deg, rgba(255,255,255,.0), rgba(255,255,255,.9), rgba(255,255,255,.0));
    transition: right .34s cubic-bezier(.22,.61,.36,1);
  }
  &:hover:after{ right: 0; }
  /* glow on hover */
  &:hover{
    text-shadow: 0 6px 28px rgba(255,255,255,.25), 0 0 22px rgba(255,255,255,.15);
    transform: translateY(-1px);
  }

  @media (max-width: 768px){
    font-size: clamp(22px, 7.6vw, 34px);
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  flex-wrap: wrap;
  opacity: .9;
  font-size: .82rem;
  padding-left: clamp(32px, 8vw, 120px);

  @media (max-width: 768px) {
    padding-left: clamp(12px, 4vw, 20px);
  }
`;

const FooterChip = styled.span`
  border: 1px solid rgba(255,255,255,.18);
  background: rgba(255,255,255,.06);
  border-radius: 999px;
  padding: .42rem .68rem;
`;

const sheetTransition = { duration: 0.6, ease: [0.22, 0.61, 0.36, 1] };
const itemStagger = { delayChildren: 0.1, staggerChildren: 0.06 };
const itemMotion = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } }
};

export default function MenuOverlay({ open, onClose }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const firstBtnRef = useRef(null);

  const items = useMemo(
    () => [
      { label: "HOME", to: "/" },
      ...PRIMARY_NAV_ITEMS.map((item) => ({
        label: t(item.labelKey),
        to: item.to,
      })),
    ],
    [t]
  );

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const stop = () => window.lenis?.stop?.();
    const start = () => window.lenis?.start?.();
    if (open) {
      document.body.style.overflow = "hidden";
      stop();
      document.documentElement.setAttribute('data-overlay-open', '');
      setTimeout(() => firstBtnRef.current?.focus?.(), 20);
      window.addEventListener("keydown", onKey);
    }
    return () => {
      document.body.style.overflow = "";
      start();
      window.removeEventListener("keydown", onKey);
      document.documentElement.removeAttribute('data-overlay-open');
    };
  }, [open, onClose]);

  const go = (to, label) => {
    trackEvent("navigation_click", {
      path: window.location.pathname,
      target: to,
      label,
      nav_type: "menu",
    });
    onClose();
    navigate(to);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <Backdrop
            aria-label="Close menu"
            aria-hidden="true"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.25 } }}
          />
          <Shell
            initial="hidden"
            animate="show"
            exit="hidden"
          >
            <Sheet
              role="dialog"
              aria-modal="true"
              aria-label="Site menu"
              initial={{ clipPath: "circle(0% at 95% 4%)" }}
              animate={{ clipPath: "circle(150% at 95% 4%)", transition: sheetTransition }}
              exit={{ clipPath: "circle(0% at 95% 4%)", transition: { ...sheetTransition, duration: 0.45 } }}
            >
              <Grid>
                <Row>
                  <Brand>
                    <img src="/makdis_logo.png" alt="MAKDIS" />
                    <span>MENU</span>
                  </Brand>
                  <CloseBtn
                    aria-label="Close"
                    whileTap={{ scale: 0.96 }}
                    onClick={onClose}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <path d="M6 6l12 12M18 6L6 18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </CloseBtn>
                </Row>

                <ListWrap>
                  <List variants={itemStagger}>
                    {items.map((it, idx) => (
                      <Item key={it.to} variants={itemMotion}>
                        <LinkBtn
                          ref={idx === 0 ? firstBtnRef : undefined}
                          onClick={() => go(it.to, it.label)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {it.label}
                        </LinkBtn>
                      </Item>
                    ))}
                  </List>
                </ListWrap>

                <Footer>
                  <FooterChip>{i18n.language.startsWith("nl") ? "NL" : "EN"}</FooterChip>
                  <FooterChip>© {new Date().getFullYear()} MAKDIS</FooterChip>
                </Footer>
              </Grid>
            </Sheet>
          </Shell>
        </>
      )}
    </AnimatePresence>
  );
}
